/**
 * FE-S3a — render GET /sa/platform/domains as operator-facing tables (no secrets, no env jargon).
 */

/**
 * @param {unknown} v
 * @returns {string}
 */
function flagCell(v) {
  if (v === true) return "on";
  if (v === false) return "off";
  return "—";
}

/**
 * @param {unknown} raw
 * @returns {string}
 */
function softStoreLabel(raw) {
  const s = String(raw || "").trim().toLowerCase();
  if (!s) return "—";
  if (s === "pg" || s === "postgres" || s === "postgresql") return "Primary database";
  if (s.includes("pg_write_sor") || s === "pg_sor") return "Primary database";
  if (s === "sqlite" || s.includes("sqlite")) return "Legacy local store";
  if (s.includes("redis")) return "Fast cache";
  if (s.includes("b2") || s.includes("object")) return "Object storage";
  return String(raw);
}

/**
 * @param {unknown} id
 * @returns {string}
 */
function softDomainLabel(id) {
  const s = String(id || "").trim();
  if (!s) return "—";
  const map = {
    device_ownership: "Device ownership",
    device_acl: "Device access",
    dashboard_users: "Console users",
    dashboard_users_local: "Local user mirror",
    admin_user_scopes: "Child admin access",
    tenant_delegations: "Delegated access",
    notification_group_shares: "Group shares",
    role_policies: "Role policies",
    billing: "Billing",
    audit: "Audit trail",
    alarm_feed: "Map alarm feed",
    export_jobs: "Export / report jobs",
    messages: "Device messages",
    ota_jobs: "Firmware update jobs",
    scheduled_commands: "Scheduled commands",
  };
  if (map[s]) return map[s];
  return s
    .replace(/\bOTA\b/gi, "Firmware")
    .replace(/[_-]+/g, " ")
    .replace(/\b\w/g, (c) => c.toUpperCase());
}

/**
 * Soften residual / honesty notes that mention SQLite / PG / MQTT / Redis.
 * @param {unknown} notes
 * @returns {string}
 */
function softDomainNotes(notes) {
  let s = String(notes || "").trim();
  if (!s) return "—";
  s = s
    .replace(/\bSQLite\b/gi, "legacy local store")
    .replace(/\bPostgres(?:ql)?\b|\bPG\b/g, "primary database")
    .replace(/\bMQTT\b/g, "device link")
    .replace(/\bRedis\b/gi, "fast cache")
    .replace(/\bOTA\b/g, "Firmware")
    .replace(/\bEXPORT_JOBS_PG\b/g, "primary-database exports")
    .replace(/\bMESSAGES_PG\b/g, "primary-database messages")
    .replace(/\bDSN\b/g, "connection");
  return s;
}

/**
 * @param {unknown} raw
 * @returns {string}
 */
function softConsoleContract(raw) {
  const s = String(raw || "").trim();
  if (!s) return "—";
  return s
    .replace(/paginated_devices/gi, "Paged device list")
    .replace(/redis_read_cache/gi, "read cache")
    .replace(/\s*\+\s*/g, " · ");
}

/**
 * @param {Record<string, unknown>} pack
 * @param {{ escapeHtml: (s: unknown) => string }} deps
 * @returns {string}
 */
export function buildPlatformDomainsTablesHtml(pack, deps) {
  const esc = deps && typeof deps.escapeHtml === "function" ? deps.escapeHtml : (s) => String(s ?? "");
  const p = pack && typeof pack === "object" ? pack : {};
  const control = Array.isArray(p.control_plane_domains) ? p.control_plane_domains : [];
  const residual = Array.isArray(p.sqlite_residual_domains) ? p.sqlite_residual_domains : [];
  const ds = p.device_state_pg_flags && typeof p.device_state_pg_flags === "object" ? p.device_state_pg_flags : {};
  const honesty = p.honesty && typeof p.honesty === "object" ? p.honesty : {};

  const overview = `<div class="ops-fcm-mig-cols glass-panel glass-panel--inset" style="margin-bottom:14px;padding:12px 14px">
    <div class="ops-fcm-mig-col">
      <p class="muted ops-fcm-mig-col-title">Primary database</p>
      <dl class="ops-fcm-mig-dl">
        <div class="ops-fcm-mig-row"><dt>Platform database</dt><dd class="mono">${esc(flagCell(p.platform_pg_enabled))}</dd></div>
        <div class="ops-fcm-mig-row"><dt>Connection ready</dt><dd class="mono">${esc(flagCell(p.platform_pg_dsn_configured))}</dd></div>
        <div class="ops-fcm-mig-row"><dt>Device state store</dt><dd class="mono">${esc(flagCell(p.device_state_pg_enabled))}</dd></div>
      </dl>
    </div>
    <div class="ops-fcm-mig-col">
      <p class="muted ops-fcm-mig-col-title">Device state</p>
      <dl class="ops-fcm-mig-dl">
        <div class="ops-fcm-mig-row"><dt>Active</dt><dd class="mono">${esc(flagCell(ds.enabled))}</dd></div>
        <div class="ops-fcm-mig-row"><dt>Writing to both stores</dt><dd class="mono">${esc(flagCell(ds.mirror_write))}</dd></div>
        <div class="ops-fcm-mig-row"><dt>Writes to primary store</dt><dd class="mono">${esc(flagCell(ds.write_sor))}</dd></div>
        <div class="ops-fcm-mig-row"><dt>Legacy write stopped</dt><dd class="mono">${esc(flagCell(ds.sqlite_write_disabled))}</dd></div>
      </dl>
    </div>
    <div class="ops-fcm-mig-col">
      <p class="muted ops-fcm-mig-col-title">Readiness claims</p>
      <dl class="ops-fcm-mig-dl">
        <div class="ops-fcm-mig-row"><dt>Large-fleet speed target</dt><dd class="mono">${esc(flagCell(honesty.claim_10k_http_slo))}</dd></div>
        <div class="ops-fcm-mig-row"><dt>Full primary-store readiness</dt><dd class="mono">${esc(flagCell(honesty.claim_full_pg))}</dd></div>
        <div class="ops-fcm-mig-row"><dt>Console data path</dt><dd class="mono">${esc(softConsoleContract(honesty.console_contract))}</dd></div>
      </dl>
    </div>
  </div>`;

  const controlRows = control
    .map((row) => {
      const flags = (row && row.pg_flags) || {};
      return `<tr>
        <td>${esc(softDomainLabel((row && row.id) || ""))}</td>
        <td>${esc(softStoreLabel((row && row.effective_sor) || ""))}</td>
        <td>${esc(flagCell(flags.read))}</td>
        <td>${esc(flagCell(flags.write_sor))}</td>
        <td>${esc(flagCell(flags.sqlite_write_disabled))}</td>
        <td>${esc(flagCell(flags.mirror_write))}</td>
      </tr>`;
    })
    .join("");

  const residualRows = residual
    .map(
      (row) => `<tr>
        <td>${esc(softDomainLabel((row && (row.label || row.id)) || ""))}</td>
        <td class="muted">${esc(softDomainNotes((row && row.notes) || ""))}</td>
      </tr>`,
    )
    .join("");

  return `${overview}
    <h3>Active data domains</h3>
    <p class="muted" style="margin:0 0 8px;font-size:12.5px">Where each capability reads and writes. No connection secrets are shown.</p>
    <table class="table">
      <thead><tr>
        <th>Domain</th><th>Primary store</th><th>Read</th><th>Write primary</th><th>Legacy stopped</th><th>Copy writes</th>
      </tr></thead>
      <tbody>${controlRows || `<tr><td colspan="6" class="muted">No domains listed.</td></tr>`}</tbody>
    </table>
    <h3 style="margin-top:16px">Still on older storage</h3>
    <p class="muted" style="margin:0 0 8px;font-size:12.5px">These areas still use legacy local storage until their migration wave completes.</p>
    <table class="table">
      <thead><tr><th>Area</th><th>Notes</th></tr></thead>
      <tbody>${residualRows || `<tr><td colspan="2" class="muted">None listed.</td></tr>`}</tbody>
    </table>`;
}
